import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./i18n";
import { useAuth } from "./hooks/useAuth";
import { AppShell } from "./components/layout/AppShell";
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Dashboard } from "./pages/Dashboard";
import { Clients } from "./pages/Clients";
import { ClientDetail } from "./pages/ClientDetail";
import { Schedule } from "./pages/Schedule";
import { HACCP } from "./pages/HACCP";
import { InspectionForm } from "./pages/InspectionForm";
import { QRPrintSheet } from "./pages/QRPrintSheet";
import { Billing } from "./pages/Billing";
import { Team } from "./pages/Team";
import { Integrations } from "./pages/Integrations";
import { Legal } from "./pages/Legal";

function AppRoutes() {
  const { user, loading, login, register, logout } = useAuth();
  const location = useLocation();
  const returnTo = (location.state as any)?.from || "/app";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-950">
        <div className="w-8 h-8 border-2 border-navy-700 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const protect = (page: React.ReactNode) =>
    user ? (
      <AppShell userName={user.name ?? user.email} userRole={user.role} onLogout={logout}>
        {page}
      </AppShell>
    ) : <Navigate to="/login" replace state={{ from: location.pathname }} />;

  const ownerOnly = (page: React.ReactNode) =>
    user?.role === "owner" ? protect(page) : <Navigate to="/app" replace />;

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={user ? <Navigate to="/app" replace /> : <Landing />} />
      <Route path="/login" element={user ? <Navigate to={returnTo} replace /> : <Login onLogin={login} />} />
      <Route path="/register" element={user ? <Navigate to={returnTo} replace /> : <Register onRegister={register} />} />
      <Route path="/regulamin" element={<Legal />} />
      <Route path="/prywatnosc" element={<Legal />} />

      {/* Protected app routes */}
      <Route path="/app" element={protect(<Dashboard />)} />
      <Route path="/app/clients" element={protect(<Clients />)} />
      <Route path="/app/clients/:id" element={protect(<ClientDetail />)} />
      <Route path="/app/schedule" element={protect(<Schedule />)} />
      <Route path="/app/haccp" element={protect(<HACCP />)} />
      <Route path="/app/inspect/:stationId" element={protect(<InspectionForm />)} />
      <Route path="/app/qr/:locationId" element={protect(<QRPrintSheet />)} />

      {/* Owner-only settings routes */}
      <Route path="/app/settings/billing" element={ownerOnly(<Billing />)} />
      <Route path="/app/settings/team" element={ownerOnly(<Team />)} />
      <Route path="/app/settings/integrations" element={ownerOnly(<Integrations />)} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to={user ? "/app" : "/"} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

const root = createRoot(document.getElementById("root")!);
root.render(<App />);
